import React from "react";
import {
  Category,
  DateRange,
  Description,
  Preview,
  Subscriptions,
  Verified,
  YouTube,
} from "@mui/icons-material";
import LinkIcon from "@mui/icons-material/Link";
import {
  Link,
  Grid,
  Avatar,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";

function StatCard({ icon, label, value }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        bgcolor: 'white',
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        border: '1px solid rgba(0,0,0,0.05)',
        flex: 1,
        minWidth: 0,
      }}
    >
      <Box sx={{ color: '#FF0000', mt: 0.2, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'Quicksand', fontWeight: 700, color: '#1a1a2e', fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

function YoutubeCollapes({ profile }) {
  const handle = profile.youtubeData?.handle || profile.youtubeData?.channel_id || profile.username || '';
  const initials = (handle.charAt(0).toUpperCase() + handle.charAt(1).toLowerCase()) || 'YT';
  const avatarSrc = profile.youtubeData?.channel?.info?.avatar_url || null;

  const subCount = profile.youtubeData?.statistics?.subscriberCount;
  const formattedSubs = subCount
    ? Number(subCount) >= 1000000
      ? `${(Number(subCount) / 1000000).toFixed(1)}M`
      : Number(subCount) >= 1000
        ? `${(Number(subCount) / 1000).toFixed(0)}K`
        : subCount
    : '—';

  const viewCount = profile.youtubeData?.statistics?.viewCount
    ? Number(profile.youtubeData.statistics.viewCount).toLocaleString('en-IN')
    : '—';

  const joinDate = profile.youtubeData?.statistics?.joinedDate
    ? new Date(parseInt(profile.youtubeData.statistics.joinedDate)).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

  const channelUrl = profile.youtubeData?.handle
    ? `https://www.youtube.com/${profile.youtubeData.handle}`
    : profile.youtubeData?.channel_id
      ? `https://www.youtube.com/channel/${profile.youtubeData.channel_id}`
      : profile.youtubeData?.url || '#';

  return (
    <Box
      sx={{
        background: 'linear-gradient(150deg, #f8faff 0%, #fff0f0 40%, #fff5f0 100%)',
        borderRadius: 2,
        p: 2,
        mt: 0.5,
        border: '1px solid rgba(255,0,0,0.1)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
      }}
    >
      {/* Profile header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 1.5,
          borderBottom: '1px solid rgba(255,0,0,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={avatarSrc}
            sx={{
              width: 44,
              height: 44,
              bgcolor: '#FF0000',
              color: 'white',
              fontWeight: 800,
              fontSize: 16,
              boxShadow: '0 3px 10px rgba(255,0,0,0.3)',
            }}
          >
            {initials}
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Link
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: 'none', color: '#FF0000', fontWeight: 800, fontFamily: 'Quicksand', fontSize: '0.95rem' }}
              >
                {handle}
              </Link>
              {profile.is_verified && (
                <Verified sx={{ color: '#aaa', fontSize: 15 }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Quicksand', fontWeight: 600 }}>
              {profile.youtubeData?.category || 'YouTube Creator'}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="View on YouTube">
          <Link
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', alignItems: 'center', color: '#FF0000', '&:hover': { color: '#CC0000' } }}
          >
            <YouTube sx={{ fontSize: 26 }} />
          </Link>
        </Tooltip>
      </Box>

      {/* Stats row */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <StatCard icon={<Subscriptions sx={{ fontSize: 16 }} />} label="Subscribers" value={formattedSubs} />
        <StatCard icon={<Preview sx={{ fontSize: 16 }} />} label="Total Views" value={viewCount} />
        <StatCard icon={<DateRange sx={{ fontSize: 16 }} />} label="Joined" value={joinDate} />
        <StatCard icon={<Category sx={{ fontSize: 16 }} />} label="Category" value={profile.youtubeData?.category} />
      </Box>

      {/* Channel link */}
      {channelUrl !== '#' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'white',
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            border: '1px solid rgba(0,0,0,0.05)',
            mb: profile.youtubeData?.description ? 1.5 : 0,
          }}
        >
          <LinkIcon sx={{ fontSize: 15, color: '#FF0000' }} />
          <Link
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontFamily: 'Quicksand', fontWeight: 700, color: '#FF0000', fontSize: '0.78rem', wordBreak: 'break-all' }}
          >
            {channelUrl}
          </Link>
        </Box>
      )}

      {/* Description */}
      {profile.youtubeData?.description && (
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            border: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Description sx={{ fontSize: 15, color: '#FF0000' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              About
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontFamily: 'Quicksand', color: '#374151', lineHeight: 1.6, fontSize: '0.82rem' }}>
            {profile.youtubeData.description}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default YoutubeCollapes;
